import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Excel-style multi-select dropdown with search, checkboxes, and removable chips.
 * Reusable across analytics pages (Therapeutic Areas, Pharmaceutical Forms, etc.).
 */
export default function MultiSelectDropdown({ options, selected, onToggle, onClear, onSelectAll, placeholder = 'Filter...', searchPlaceholder = 'Search...' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const keyHandler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', keyHandler); };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(f => f.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', maxWidth: 600 }}>
      {/* Trigger box */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          minHeight: 36, padding: '4px 8px', border: '1px solid var(--brd, #d1d5db)', borderRadius: 6,
          background: 'var(--card, #fff)', cursor: 'pointer', display: 'flex', flexWrap: 'wrap',
          alignItems: 'center', gap: 4, overflow: 'hidden',
        }}
      >
        {selected.size === 0 && (
          <span style={{ color: 'var(--t3, #9ca3af)', fontSize: 12 }}>{placeholder}</span>
        )}
        {Array.from(selected).slice(0, 5).map(f => (
          <span key={f} title={f} style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 500,
            background: '#156082', color: '#fff',
            maxWidth: 'calc(100% - 30px)', minWidth: 0,
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f}</span>
            <span
              onClick={(e) => { e.stopPropagation(); onToggle(f); }}
              style={{ cursor: 'pointer', flexShrink: 0, fontWeight: 700, fontSize: 13, lineHeight: 1 }}
            >&times;</span>
          </span>
        ))}
        {selected.size > 5 && (
          <span style={{ fontSize: 11, color: 'var(--t2, #6b7280)', fontWeight: 500 }}>+{selected.size - 5} more</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--t3, #9ca3af)', paddingRight: 2 }}>&#9662;</span>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          marginTop: 4, background: 'var(--card, #fff)', border: '1px solid var(--brd, #d1d5db)',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.12)', maxHeight: 320, display: 'flex', flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '8px 10px 6px', borderBottom: '1px solid #e5e7eb' }}>
            <input
              ref={searchRef}
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', border: '1px solid #d1d5db', borderRadius: 5, padding: '5px 8px',
                fontSize: 12, outline: 'none', background: '#f9fafb',
              }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>
            <button type="button" onClick={onSelectAll} style={{
              fontSize: 11, color: '#156082', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0,
            }}>Select all</button>
            <button type="button" onClick={onClear} style={{
              fontSize: 11, color: '#E97132', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0,
            }}>Clear all</button>
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--t3, #9ca3af)' }}>No matches for "{search}"</div>
            )}
            {filtered.map(f => (
              <label
                key={f}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px',
                  fontSize: 12, cursor: 'pointer', color: 'var(--navy, #0E2841)',
                  background: selected.has(f) ? '#f0f7fa' : 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0f7fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = selected.has(f) ? '#f0f7fa' : 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(f)}
                  onChange={() => onToggle(f)}
                  style={{ accentColor: '#156082', width: 14, height: 14, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: selected.has(f) ? 600 : 400 }}>{f}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
